import { designTokens } from "@itandi/itandi-bb-ui";
import { Alertv2 } from "@itandi/itandi-bb-ui/Alertv2";
import { ButtonLink } from "@itandi/itandi-bb-ui/ButtonLink";
import { Buttonv2 } from "@itandi/itandi-bb-ui/Buttonv2";
import { Dropdown } from "@itandi/itandi-bb-ui/Dropdown";
import { DropdownMenuLink } from "@itandi/itandi-bb-ui/DropdownMenuLink";
import { Flex } from "@itandi/itandi-bb-ui/Flex";
import { Form } from "@itandi/itandi-bb-ui/Form";
import { FormControl } from "@itandi/itandi-bb-ui/FormControl";
import { FormGroup } from "@itandi/itandi-bb-ui/FormGroup";
import { InputDatev2 } from "@itandi/itandi-bb-ui/InputDatev2";
import { ModalBody } from "@itandi/itandi-bb-ui/ModalBody";
import { ModalFooter } from "@itandi/itandi-bb-ui/ModalFooter";
import { ModalHeader } from "@itandi/itandi-bb-ui/ModalHeader";
import { Modalv2 } from "@itandi/itandi-bb-ui/Modalv2";
import { useNotification } from "@itandi/itandi-bb-ui/NotificationBarStore";
import { Panel } from "@itandi/itandi-bb-ui/Panel";
import { PanelBody } from "@itandi/itandi-bb-ui/PanelBody";
import { PanelHeader } from "@itandi/itandi-bb-ui/PanelHeader";
import { YokuaruBody } from "@itandi/itandi-bb-ui/YokuaruBody";
import { Redirect, useReadiedQuery } from "@itandi/tools/next";
import { parsePositiveInteger } from "@itandi/tools/vanilla";
import Link from "next/link";
import type { ComponentProps } from "react";
import { useId, useState } from "react";
import useSWRMutation from "swr/mutation";
import { DefaultV2 } from "~/components/common/layouts/DefaultV2";
import { DealLayoutHeaderClient } from "~/components/common/layouts/deals/DealLayoutHeaderClient";
import { PropertyDealLayout } from "~/components/common/layouts/property_deals/PropertyDealLayout";
import { RelatedDealListTop } from "~/components/specifics/property_deals/[id]/RelatedDealListTop";
import { RelatedDealRow } from "~/components/specifics/property_deals/[id]/RelatedDealRow";
import { RelatedDealTotalRow } from "~/components/specifics/property_deals/[id]/RelatedDealTotalRow";
import { useDeals } from "~/hooks/useDeals";
import { useIdQueryHandler } from "~/hooks/useIdQueryHandler";
import useItandiAccount from "~/hooks/useItandiAccount";
import {
	propertyDealCacheKey,
	usePropertyDeal
} from "~/hooks/usePropertyDeals";
import { useStatuses } from "~/hooks/useStatuses";
import { PATH as DEALS_PATH } from "~/pages/deals";
import { PATH as RELATED_DEAL_NEW_PATH } from "~/pages/deals/[id]/related_deals/new";
import { isSuccess } from "~/utils/api/api_handler";
import { getDownloadPath as getBillingDownloadPath } from "~/utils/api/property_deals/DealBillingFile";
import { getDownloadPath as getEstimationDownloadPath } from "~/utils/api/property_deals/DealEstimationFile";
import { bulkUpdate } from "~/utils/api/property_deals/DealExpectedAccountingDates";
import { formatHyphenDate } from "~/utils/DateUtil";
import Styles from "./Page.module.scss";

type StatusNameMap = Record<number, string>;

export function Page() {
	const { notify } = useNotification();
	const id = useReadiedQuery("id", (q) => {
		const parsed = parsePositiveInteger(q);

		if (parsed === null) {
			throw new Error("Invalid Deal ID");
		}

		return parsed;
	});
	const dealId = useIdQueryHandler("dealId");
	const { data: propertyDeal } = usePropertyDeal(id);
	const { data: statuses } = useStatuses().index();
	const { data: deal } = useDeals().show(dealId);
	const { data: session } = useItandiAccount().currentAccount();
	const isRelatedDeal = propertyDeal?.deals.some(
		(relatedDeal) => relatedDeal.id === deal?.id
	);

	const formId = useId();
	const [isBulkModalOpen, setIsBulkModalOpen] = useState(false);
	const [date, setDate] = useState<Date | null>(null);

	const { trigger, isMutating } = useSWRMutation(
		propertyDealCacheKey(id),
		async (_key, { arg }: { arg: string }) => bulkUpdate(id, arg)
	);

	const onBulkSubmit = async () => {
		const result = await trigger(formatHyphenDate(date)!);
		if (isSuccess(result)) {
			setIsBulkModalOpen(false);
			notify({ content: "保存しました。", variant: "success" });
		} else {
			notify({
				content: result.message ?? "エラーが発生しました。",
				variant: "error"
			});
		}
	};

	if (propertyDeal === null || statuses === null || session === null) {
		return null;
	}

	const isV2 = session.company?.useV2DealDetail ?? false;

	const statusMap: StatusNameMap = {};
	for (const status of statuses) {
		statusMap[status.id] = status.name;
	}

	const items = [
		{ as: Link, href: DEALS_PATH, label: "案件一覧" },
		{ label: "案件グループ" }
	] as const satisfies ComponentProps<typeof DealLayoutHeaderClient>["items"];

	return (
		<DefaultV2>
			{deal !== null && !isRelatedDeal && <Redirect url="/" />}
			{isV2 && deal !== null ? (
				<>
					<DealLayoutHeaderClient
						deal={deal}
						enableChat={false}
						isChatOpen={false}
						isV2DealDetail={isV2}
						items={items}
						messages={[]}
						useSimpleSpHeader={false}
					/>
					<YokuaruBody>
						<Panel>
							<PanelHeader
								rightArea={
									<Flex gap={designTokens["spacing/s"]}>
										<Buttonv2
											onClick={() => {
												setDate(null);
												setIsBulkModalOpen(true);
											}}
											variant="secondary"
										>
											計上予定日を一括登録
										</Buttonv2>
										<Dropdown
											button={
												<Buttonv2 variant="secondary">
													見積・請求のサマリーを出力
												</Buttonv2>
											}
										>
											<DropdownMenuLink
												href={getEstimationDownloadPath(propertyDeal.id)}
												target="_blank"
											>
												施工会社からの見積
											</DropdownMenuLink>
											<DropdownMenuLink
												href={getBillingDownloadPath(propertyDeal.id, "rentee")}
												target="_blank"
											>
												入居者への請求
											</DropdownMenuLink>
											<DropdownMenuLink
												href={getBillingDownloadPath(propertyDeal.id, "owner")}
												target="_blank"
											>
												オーナーへの請求
											</DropdownMenuLink>
										</Dropdown>
										<ButtonLink
											as={Link}
											href={RELATED_DEAL_NEW_PATH(deal.id)}
											variant="primary"
										>
											関連する案件追加
										</ButtonLink>
									</Flex>
								}
								title={`関連する案件一覧（${propertyDeal.deals.length}件）`}
							/>
							<PanelBody>
								{propertyDeal.deals.map((d) => (
									<RelatedDealRow
										deal={d}
										key={d.id}
										propertyDealId={id}
										statuses={statusMap}
									/>
								))}
								<RelatedDealTotalRow
									isV2DealDetail={isV2}
									propertyDeal={propertyDeal}
								/>
							</PanelBody>
						</Panel>
					</YokuaruBody>
					{isBulkModalOpen ? (
						<Modalv2
							isOpen
							onClose={() => {
								setIsBulkModalOpen(false);
							}}
							size="s"
						>
							<ModalHeader title="計上予定日の一括登録" />
							<ModalBody>
								<Form id={formId} onSubmit={onBulkSubmit}>
									<FormGroup variant="vertical">
										<FormControl label="計上予定日">
											<InputDatev2 onChange={setDate} value={date} />
										</FormControl>
									</FormGroup>
								</Form>
								{propertyDeal.deals.some(
									(d) => d.expectedAccountingDate !== null
								) && (
									<Alertv2 mt={designTokens["spacing/s"]} variant="warning">
										登録済みの計上予定日は上書きされます。
									</Alertv2>
								)}
							</ModalBody>
							<ModalFooter
								leftArea={
									<Buttonv2
										disabled={isMutating}
										onClick={() => {
											setIsBulkModalOpen(false);
										}}
										variant="secondary"
									>
										キャンセル
									</Buttonv2>
								}
								rightArea={
									<Buttonv2
										disabled={date === null || isMutating}
										form={formId}
										loading={isMutating}
										type="submit"
										variant="primary"
									>
										保存
									</Buttonv2>
								}
							/>
						</Modalv2>
					) : null}
				</>
			) : (
				<PropertyDealLayout items={items} propertyDeal={propertyDeal}>
					<div className={Styles.Container}>
						<RelatedDealListTop propertyDeal={propertyDeal} />
						<div className={Styles.DealList}>
							{propertyDeal.deals.map((d) => (
								<RelatedDealRow
									deal={d}
									key={d.id}
									propertyDealId={id}
									statuses={statusMap}
								/>
							))}
						</div>
						<RelatedDealTotalRow
							isV2DealDetail={isV2}
							propertyDeal={propertyDeal}
						/>
					</div>
				</PropertyDealLayout>
			)}
		</DefaultV2>
	);
}
